<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { TrackEditorEngine } from '@/lib/trackEditor/engine'
import { MATERIAL_NAMES, MATERIALS, materialsForType } from '@/lib/trackEditor/materials'
import { SURFACE_STYLES, TERRAIN_GROUPS, TERRAIN_STYLES } from '@/lib/trackEditor/trackFormat'
import { blackPicturePath, encodeWebp, exportTrackTiles, releaseCanvas, tilePreviewPath } from '@/lib/trackEditor/tileExport'
import { downloadBlob, nextFrame } from '@/lib/browser'
import { makeZip } from '@/lib/zip'
import { trackEditorBackground } from '@/lib/tracks'
import TerrainTexturePicker from '@/components/TerrainTexturePicker.vue'
import { POSE_ANCHOR_COLOR } from '@/lib/trackEditor/pose'

const wrapperEl = ref(null)
const canvasEl = ref(null)
const fileInputEl = ref(null)

let engine = null
let syncingFields = false
let editingField = false

// Obstacle types sharing the bounciness/friction/material editors — keep every type here, or its
// Properties fields end up editing a value nothing writes back to the engine.
const OBSTACLE_TYPES = ['wall', 'circle', 'rect', 'poly']

const cursor = ref('default')
const hudCoords = ref('0, 0')

const state = reactive({
  mode: 'select',
  hudStatus: 'Select mode — click to select, or drag a box to select several',
  selection: null,
  selectionCount: 0,
  selectionKeys: [],
  objectTree: [],
  canCopy: false,
  hasClipboard: false,
  canUndo: false,
  canRedo: false,
  undoCount: 0,
  redoCount: 0,
  historyRevision: 0,
})

const collapsed = reactive({
  defaults: true,
  properties: false,
  view: false,
  trackInfo: false,
})
function toggleCollapse(panel) { collapsed[panel] = !collapsed[panel] }

const trackId = ref('track_custom')
const trackName = ref('Custom Track')
const trackWidth = ref('')
const trackHeight = ref('')
const defWidth = ref(210)
const defLane = ref('main')
const magneticSnap = ref(true)
const hideObjects = ref(false)
const defObsThickness = ref(20)
const defObsRestitution = ref(0.15)
const defObsFriction = ref(400)
// What an obstacle is made of: the names come from the catalogue (lib/trackEditor/materials.js, a copy
// of the game's own) — a material added there shows up in every one of these menus at once.
const materialOptions = MATERIAL_NAMES
// The same list minus what the selected TYPE cannot be made of: a material that is ONE sprite fitted to a
// rectangle (stands, houses) is only ever offered for a rect (see materialsForType).
const obstacleMaterialOptions = computed(() => materialsForType(state.selection?.type ?? 'wall'))
// The sprites the SELECTED MATERIAL offers, and which one the selected rectangle uses: every look with
// `variants` shows its own list here, so a material added to the catalogue needs nothing in this view.
const variantOptions = computed(() => MATERIALS[obsMaterial.value]?.variants ?? [])
const obsVariant = ref(0)
// The variant's own picture, beside the choice: the canvas draws it too, but on the map a grandstand (or a
// block of houses) is a few dozen pixels wide and the variants are hard to tell apart there.
const variantPreview = computed(() => {
  const variant = variantOptions.value[obsVariant.value]
  return variant ? `/img/${variant.src}` : ''
})
// A figure marked decoration is painted into the exported picture and left out of the track file.
const decorFlag = ref(false)
// The painting layer of an obstacle or a terrain figure: empty (or 0) is the base layer, a higher
// number lies over it. Objects of the same family are ordered by it; see inPaintOrder in trackFormat.
const zIndex = ref('')
const paletteMaterial = ref('houses')
const paletteVariants = computed(() => MATERIALS[paletteMaterial.value].variants)
const paletteDrag = ref(false)
const SPRITE_DRAG_TYPE = 'application/x-gpo-track-sprite'
function startSpriteDrag(event, variant) {
  event.dataTransfer.effectAllowed = 'copy'
  event.dataTransfer.setData(SPRITE_DRAG_TYPE, JSON.stringify({ material: paletteMaterial.value, variant }))
  const image = event.currentTarget.querySelector('img')
  event.dataTransfer.setDragImage(image, image.width / 2, image.height / 2)
}
function overSpriteCanvas(event) {
  if (!Array.from(event.dataTransfer.types).includes(SPRITE_DRAG_TYPE)) return
  event.preventDefault()
  event.dataTransfer.dropEffect = 'copy'
  paletteDrag.value = true
}
function dropSprite(event) {
  paletteDrag.value = false
  const data = event.dataTransfer.getData(SPRITE_DRAG_TYPE)
  if (!data) return
  event.preventDefault()
  try {
    const { material, variant } = JSON.parse(data)
    engine?.placeSpriteObstacle(material, variant, event.clientX, event.clientY)
  } catch { /* Ignore unsupported drag data. */ }
}
const defObsMaterial = ref('wall')
const defPolyThickness = ref(8)

const segWidth = ref(180)
// A segment is either on the main track or the pit lane: shown as a checkbox, like a road area's.
const segPit = ref(false)
// The look the selected piece of asphalt is painted with: one entry per style the game's painter
// knows (SURFACE_STYLES), which today is the single "GPO" one.
const surfaceOptions = SURFACE_STYLES
const segSurface = ref(SURFACE_STYLES[0])

// Road areas are surfaces: their properties are the look they are painted with and whether the area
// IS the pit lane (yellow line in game, and the game's pit logic on it instead of on a `pit` branch).
const areaPit = ref(false)
const areaSurface = ref(SURFACE_STYLES[0])

// Terrain areas: the ground figures (rectangles, circles and free areas). Their names come from the
// game's own simulation catalogue (trackFormat.TERRAIN_STYLES, kept in step with physicsConfig.TERRAINS)
// and the picker lists them by family, each with its own texture (trackFormat.TERRAIN_GROUPS).
const defTerrain = ref('grass_dirt')
const terrainValue = ref(TERRAIN_STYLES[0])
// The ground of the whole map outside the road and the terrain figures (trackFormat.groundTerrainOf):
// it is both what the base surface looks like and what the cars drive on there, so it can be any
// terrain — a track can be sand or snow from edge to edge.
const groundTerrain = ref(TERRAIN_STYLES[0])
// Whether the trees scattered around the map are drawn, inside it and in the band outside it.
const trees = ref(true)
// Whether the game draws the woodland around the map: a track property, not a tool default, but it sits
// with the other track-wide choices (see engine.setBorderTrees). Off unless a track asks for it.
const borderTrees = ref(false)
// Whether the selected figure fades into the ground it borders. The snapshot already resolves the choice
// (the figure's own, or the default its terrain carries — see TERRAIN_BLEND_DEFAULTS), so the checkbox
// follows a change of terrain on its own: no second copy to keep in step.
const terrainBlend = computed({
  get: () => (state.selection?.type === 'terrain' ? state.selection.blend : true),
  set: (on) => { if (state.selection?.type === 'terrain') engine?.setTerrainBlend(state.selection.id, on) },
})

const obsRestitution = ref(0.15)
const obsFriction = ref(400)
const obsMaterial = ref('wall')
const wallThickness = ref(20)
const polyThickness = ref(8)
const sgStaggerPx = ref(20)
const pitCrewSide = ref('auto')
const pitCrewOffset = ref(24)
// Which way the cars face in the pit box. The panel shows the four cases as one choice; the file
// keeps 'auto' | 'forward' | 'backward' | an angle in radians, so the angle is entered in degrees
// (like the rectangle's own angle) and converted on the way out (see pitFacing in engine.js).
const pitDirection = ref('auto')
const pitDirectionDeg = ref(0)
const cpTiming = ref(false)
const circleRadius = ref(50)
const rectWidth = ref(100)
const rectHeight = ref(100)
const rectAngle = ref(0)
// The pose of the selected element, edited as absolute values: X/Y place its green point, the angle
// turns the element around that same point (see lib/trackEditor/pose.js).
const poseX = ref(0)
const poseY = ref(0)
const poseAngleDeg = ref(0)
// One element at a time: the fields write onto every selected element of that type, so with two of them
// an X would stack both onto the same point.
const canEditPose = computed(() => state.selectionCount === 1 && state.selection?.x !== undefined)
// Info ▸ Controls: the keyboard/mouse cheatsheet, shown in a dialog.
const controlsDialog = ref(false)
const historyDialog = ref(false)
const historyLimit = ref(20)
const historyLimitDraft = ref(20)
const HISTORY_LIMIT_KEY = 'gpo-track-editor-history-limit'

function openHistorySettings() {
  historyLimitDraft.value = historyLimit.value
  historyDialog.value = true
}
function saveHistorySettings() {
  engine.setHistoryLimit(historyLimitDraft.value)
  historyLimit.value = engine.historyLimit
  try { localStorage.setItem(HISTORY_LIMIT_KEY, String(historyLimit.value)) } catch { /* Session setting still applies. */ }
  historyDialog.value = false
}
function undo() { engine?.undo() }
function redo() { engine?.redo() }
function historyField(event) {
  return event.target.matches('input[type="number"], input[type="text"], select')
}
function beginFieldEdit(event) {
  if (historyField(event)) { editingField = true; engine?.beginHistoryEdit() }
}
function endFieldEdit(event) {
  if (historyField(event)) { editingField = false; engine?.endHistoryEdit() }
}

// Synchronous write-back lets snapshots refresh fields without creating phantom edits on Undo.
function watchField(source, callback) {
  watch(source, value => { if (!syncingFields) callback(value) }, { flush: 'sync' })
}

// The picture to show behind the drawing while a track is worked on: a working background of its own,
// kept apart from the picture the game shows (`img/tracks/editor_backgrounds/`), so the editor never
// shows the finished look it is about to produce. Follows the id being edited (see trackEditorBackground).
let backgroundId = null

async function loadBackground() {
  const id = trackId.value?.trim() || null
  if (id === backgroundId) return
  backgroundId = id
  const url = id ? await trackEditorBackground(id) : null
  if (backgroundId === id) engine?.setBackgroundImageUrl(url)
}

/**
 * The engine draws materials that are one sprite (a stand) but cannot fetch anything itself, so it asks
 * for the srcs it is about to draw and gets the pictures back — the same split the game's painter uses
 * (`materialImages` + `opts.images`). Only what's on screen is downloaded.
 */
function loadMaterialImages(srcs) {
  for (const src of srcs) {
    const img = new Image()
    img.onload = () => engine?.setMaterialImages({ [src]: img })
    img.src = `/img/${src}`
  }
}

/**
 * The sprites a picture needs, WAITED on: loadMaterialImages above fires and forgets (the editor canvas
 * can repaint when they land), while a picture has to have them all before it starts painting.
 */
async function loadImagesForPicture(srcs) {
  const images = {}
  await Promise.all(srcs.map((src) => new Promise((resolve) => {
    const img = new Image()
    img.onload = () => { images[src] = img; resolve() }
    img.onerror = resolve
    img.src = `/img/${src}`
  })))
  engine?.setMaterialImages(images) // one render, and the editor keeps them from here on
}

function handleChange(snapshot) {
  const backgroundChanged = trackId.value !== snapshot.trackId || state.historyRevision !== snapshot.historyRevision
  const selectionChanged = state.selection?.type !== snapshot.selection?.type
    || state.selection?.id !== snapshot.selection?.id
    || state.selectionCount !== snapshot.selectionCount
    || state.historyRevision !== snapshot.historyRevision;
  const previousSync = syncingFields
  syncingFields = true
  try {
    state.mode = snapshot.mode
    state.hudStatus = snapshot.hudStatus
    state.selection = snapshot.selection
    state.selectionCount = snapshot.selectionCount
    state.selectionKeys = snapshot.selectionKeys
    state.objectTree = snapshot.objectTree
    state.canCopy = snapshot.canCopy
    state.hasClipboard = snapshot.hasClipboard
    state.canUndo = snapshot.canUndo
    state.canRedo = snapshot.canRedo
    state.undoCount = snapshot.undoCount
    state.redoCount = snapshot.redoCount
    state.historyRevision = snapshot.historyRevision
    historyLimit.value = snapshot.historyLimit
    trackId.value = snapshot.trackId
    trackName.value = snapshot.trackName
    trackWidth.value = snapshot.manualWidth ?? ''
    trackHeight.value = snapshot.manualHeight ?? ''
    defWidth.value = snapshot.defWidth
    defLane.value = snapshot.defLane
    defObsThickness.value = snapshot.defObsThickness
    defObsRestitution.value = snapshot.defObsRestitution
    defObsFriction.value = snapshot.defObsFriction
    defObsMaterial.value = snapshot.defObsMaterial
    defPolyThickness.value = snapshot.defPolyThickness
    defTerrain.value = snapshot.defTerrain
    groundTerrain.value = snapshot.groundTerrain
    trees.value = snapshot.trees
    borderTrees.value = snapshot.borderTrees
    magneticSnap.value = snapshot.magneticSnap
    hideObjects.value = snapshot.hideObjects
    if (!editingField || selectionChanged) syncSelectionFields()
  } finally { syncingFields = previousSync }
  if (backgroundChanged) loadBackground()
}

function syncSelectionFields() {
  const sel = state.selection
  if (sel && sel.x !== undefined) {
    poseX.value = sel.x
    poseY.value = sel.y
    poseAngleDeg.value = sel.angleDeg ?? 0
  }
  if (sel && (OBSTACLE_TYPES.includes(sel.type) || sel.type === 'terrain')) {
    decorFlag.value = !!sel.decor
    zIndex.value = sel.z ?? ''
  }
  if (sel?.type === 'seg') {
    segWidth.value = sel.width
    segPit.value = (sel.lane ?? 'main') === 'pit'
    segSurface.value = sel.surface
  } else if (sel?.type === 'wall') {
    wallThickness.value = state.selection.thickness
    obsRestitution.value = state.selection.restitution
    obsFriction.value = state.selection.friction
    obsMaterial.value = state.selection.material
  } else if (state.selection?.type === 'circle') {
    circleRadius.value = state.selection.radius
    obsRestitution.value = state.selection.restitution
    obsFriction.value = state.selection.friction
    obsMaterial.value = state.selection.material
  } else if (state.selection?.type === 'rect') {
    rectWidth.value = state.selection.width
    rectHeight.value = state.selection.height
    rectAngle.value = state.selection.angleDeg
    obsRestitution.value = state.selection.restitution
    obsFriction.value = state.selection.friction
    obsMaterial.value = state.selection.material
    obsVariant.value = state.selection.variant ?? 0
  } else if (state.selection?.type === 'sg') {
    sgStaggerPx.value = state.selection.staggerPx
  } else if (state.selection?.type === 'pit') {
    pitCrewSide.value = state.selection.crewSide ?? 'auto'
    pitCrewOffset.value = state.selection.crewOffset ?? 24
    const dir = state.selection.direction ?? 'auto'
    pitDirection.value = typeof dir === 'number' ? 'angle' : dir
    pitDirectionDeg.value = typeof dir === 'number' ? Math.round(dir * 180 / Math.PI) : 0
  } else if (state.selection?.type === 'cp') {
    cpTiming.value = !!state.selection.timing
  } else if (state.selection?.type === 'poly') {
    polyThickness.value = state.selection.thickness
    obsRestitution.value = state.selection.restitution
    obsFriction.value = state.selection.friction
    obsMaterial.value = state.selection.material
  } else if (state.selection?.type === 'road') {
    areaPit.value = !!state.selection.pit
    areaSurface.value = state.selection.surface
  } else if (state.selection?.type === 'terrain') {
    terrainValue.value = state.selection.terrain
  }
}

watchField(trackId, (v) => {
  if (engine && engine.trackId !== v) engine.setTrackId(v)
  loadBackground()
})
watchField(trackName, (v) => engine?.setTrackName(v))
watchField(trackWidth, (v) => engine?.setManualWidth(parseInt(v) || 0))
watchField(trackHeight, (v) => engine?.setManualHeight(parseInt(v) || 0))
watchField(defWidth, (v) => engine?.setDefaultWidth(v))
watchField(defLane, (v) => engine?.setDefaultLane(v))
watchField(defObsThickness, (v) => engine?.setDefaultObsThickness(v))
watchField(defObsRestitution, (v) => engine?.setDefaultObsRestitution(v))
watchField(defObsFriction, (v) => engine?.setDefaultObsFriction(v))
watchField(defObsMaterial, (v) => engine?.setDefaultObsMaterial(v))
watchField(defPolyThickness, (v) => engine?.setDefaultPolyThickness(v))
watchField(segWidth, (v) => { if (state.selection?.type === 'seg') engine.setSegWidth(state.selection.id, v) })
watchField(segPit, (v) => { if (state.selection?.type === 'seg') engine.setSegLane(state.selection.id, v ? 'pit' : 'main') })
watchField(segSurface, (v) => { if (state.selection?.type === 'seg') engine.setSurface('seg', state.selection.id, v) })
watchField(areaPit, (v) => { if (state.selection?.type === 'road') engine.setAreaPit(state.selection.id, v) })
watchField(areaSurface, (v) => { if (state.selection?.type === 'road') engine.setSurface('road', state.selection.id, v) })
// New terrain figures take `defTerrain`; changing the SELECTED one writes its own terrain.
watchField(defTerrain, (v) => engine?.setDefaultTerrain(v))
watchField(groundTerrain, (v) => engine?.setGroundTerrain(v))
watchField(trees, (v) => engine?.setTrees(v))
watchField(borderTrees, (v) => engine?.setBorderTrees(v))
watchField(terrainValue, (v) => { if (state.selection?.type === 'terrain') engine.setTerrain(state.selection.id, v) })
watchField(obsRestitution, (v) => {
  if (OBSTACLE_TYPES.includes(state.selection?.type)) engine.setObsRestitution(state.selection.type, state.selection.id, v)
})
watchField(obsFriction, (v) => {
  if (OBSTACLE_TYPES.includes(state.selection?.type)) engine.setObsFriction(state.selection.type, state.selection.id, v)
})
watchField(obsMaterial, (v) => {
  if (OBSTACLE_TYPES.includes(state.selection?.type)) engine.setObsMaterial(state.selection.type, state.selection.id, v)
  // A stand's rectangle is fitted to the sprite's proportions by the engine (see fitStand), so the two
  // size fields have to follow, or the panel would show a shape that is not the one on the canvas.
  syncRectFields()
})
watchField(obsVariant, (v) => {
  if (state.selection?.type === 'rect') engine.setObsVariant('rect', state.selection.id, v)
  syncRectFields()
})
watchField(decorFlag, (v) => {
  const sel = state.selection
  if (sel && (OBSTACLE_TYPES.includes(sel.type) || sel.type === 'terrain')) engine.setDecor(sel.type, sel.id, v)
})
watchField(zIndex, (v) => {
  const sel = state.selection
  if (sel && (OBSTACLE_TYPES.includes(sel.type) || sel.type === 'terrain')) engine.setZ(sel.type, sel.id, v)
})
watchField(wallThickness, (v) => { if (state.selection?.type === 'wall') engine.setWallThickness(state.selection.id, v) })
watchField(polyThickness, (v) => { if (state.selection?.type === 'poly') engine.setPolyThickness(state.selection.id, v) })
watchField(sgStaggerPx, (v) => { if (state.selection?.type === 'sg') engine.setSgStaggerPx(v) })
watchField(pitCrewSide, (v) => { if (state.selection?.type === 'pit') engine.setPitCrewSide(v) })
watchField(pitCrewOffset, (v) => { if (state.selection?.type === 'pit') engine.setPitCrewOffset(v) })
// The two watchers of one choice: switching to 'angle' must push the degrees in use right away, and
// editing the degrees while on 'angle' must push them too (the engine takes radians).
watchField(pitDirection, (v) => {
  if (state.selection?.type !== 'pit') return
  engine.setPitDirection(v === 'angle' ? pitDirectionDeg.value * Math.PI / 180 : v)
})
watchField(pitDirectionDeg, (v) => {
  if (state.selection?.type === 'pit' && pitDirection.value === 'angle') engine.setPitDirection(v * Math.PI / 180)
})
watchField(cpTiming, (v) => { if (state.selection?.type === 'cp') engine.setCpTiming(state.selection.id, v) })
watchField(circleRadius, (v) => { if (state.selection?.type === 'circle') engine.setCircleRadius(state.selection.id, v) })
watchField(rectWidth, (v) => {
  if (state.selection?.type !== 'rect') return
  engine.setRectWidth(state.selection.id, v)
  syncRectFields()
})
watchField(rectHeight, (v) => {
  if (state.selection?.type !== 'rect') return
  engine.setRectHeight(state.selection.id, v)
  syncRectFields()
})
watchField(rectAngle, (v) => { if (state.selection?.type === 'rect') engine.setRectAngleDeg(state.selection.id, v) })
watchField(poseX, (v) => { if (canEditPose.value) engine.setPoseX(state.selection.type, state.selection.id, v) })
watchField(poseY, (v) => { if (canEditPose.value) engine.setPoseY(state.selection.type, state.selection.id, v) })
watchField(poseAngleDeg, (v) => { if (canEditPose.value) engine.setPoseAngleDeg(state.selection.type, state.selection.id, v) })

const treeExpanded = reactive({ road: true, logic: true, obstacles: true, decor: true, terrain: true })
function isTreeSelected(item) {
  return state.selectionKeys.includes(item.key)
}
function selectTreeItem(item) {
  engine?.selectObject(item.type, item.id)
  collapsed.properties = false
}
function hoverTreeItem(item) { engine?.setHovered(item?.type ?? null, item?.id) }
function selectMode() { engine?.setMode('select') }
function setMode(m) { engine.toggleMode(m) }

/**
 * Reads the selected rectangle's size and sprite variant back from the engine.
 *
 * A rectangle that IS a sprite is locked to its picture's proportions and the engine is the one that keeps
 * them (its setRectWidth/Height, setObsMaterial, setObsVariant and fitSprite all move the other side), so
 * after anything that can change the shape the panel follows the canvas instead of disagreeing with it.
 */
function syncRectFields() {
  if (state.selection?.type !== 'rect') return
  const o = engine?.getObj?.('rect', state.selection.id)
  if (!o) return
  const previousSync = syncingFields
  syncingFields = true
  try {
    rectWidth.value = Math.round(o.width)
    rectHeight.value = Math.round(o.height)
    obsVariant.value = o.variant ?? 0
  } finally { syncingFields = previousSync }
}
function selectStartingGrid() {
  if (engine.sg) { engine.setMode('select'); engine.setSelected('sg', 'sg') }
  else setMode('addSg')
}
function selectPitBox() {
  if (engine.pit) { engine.setMode('select'); engine.setSelected('pit', 'pit') }
  else setMode('addPit')
}

function deleteSelected() { engine.deleteSelected() }
function copySelected() { engine.copySelected() }
function pasteClipboard() { engine.pasteClipboard() }
function flipHorizontal() { engine.flipSelectedHorizontal() }
function curveSeg() { engine.curveSelectedSeg() }
function straightenSeg() { engine.straightenSelectedSeg() }
function straightenNode() {
  const sel = state.selection
  if (sel && sel.node != null) engine.straightenNode(sel.type, sel.id)
}
function curveNode() {
  const sel = state.selection
  if (sel && sel.node != null) engine.curveNodes(sel.type, sel.id)
}
function curveAllNodes() {
  const sel = state.selection
  if (sel) engine.curveNodes(sel.type, sel.id, true)
}
function flipDir() { engine.flipStartDir() }
function toggleSgFlip() { engine.setSgFlip(!state.selection.flip) }
function toggleSnap() { engine.setMagneticSnap(!magneticSnap.value) }
function toggleHideObjects() { engine.setHideObjects(!hideObjects.value) }
function fitView() { engine.fitView() }

/**
 * Recomputes the game area so that the whole track is inside it: the size it needs, and — since the
 * area starts at the world origin — a move of the track when some of it lies left of or above that
 * corner. That move rewrites every coordinate and the complete change can be undone.
 */
function fitGameArea() {
  const plan = engine.gameAreaPlan()
  if (!plan) return
  if ((plan.dx || plan.dy) && !confirm(
    `Part of the track lies outside the game area, which starts at the world origin (the top-left corner of the dashed box): ` +
    `the track has to be moved by ${plan.dx}, ${plan.dy} to fit. Continue?`
  )) return
  engine.fitGameArea()
  engine.fitView()
  // Set after the engine's own notify, so it is what the HUD shows until the next change.
  state.hudStatus = `Game area ${plan.width} × ${plan.height}`
    + (plan.dx || plan.dy ? ` — track moved by ${plan.dx}, ${plan.dy}` : '')
}

function newTrack() {
  const hasContent = engine.segs.length + engine.cps.length > 0
  if (hasContent && !confirm('Start a new empty track? The current track will be replaced; Undo can restore it.')) return
  engine.newTrack()
}

function triggerImport() { fileInputEl.value?.click() }
function onImportFile(e) {
  const file = e.target.files[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = (ev) => {
    try { engine.importTrack(JSON.parse(ev.target.result)) }
    catch { alert('Could not parse the JSON file.') }
  }
  reader.readAsText(file)
  e.target.value = ''
}

/** What the editor is busy with, or null (see the export overlay). */
const exporting = ref(null)

/**
 * Writes the folder the game reads: the picture a track's whole look is painted into, the JSON that names
 * it and stamps the geometry it was painted from, and the two pictures the in-game track picker can show
 * on a track's card (see the game's clientConfig.TRACK_PICKER). The paths inside the archive are the ones
 * those files have under the game's `src/`, so it is unzipped straight into it.
 */
async function exportTrack() {
  if (exporting.value) return
  exporting.value = 'Painting the track…'
  engine.setBusy(true)
  try {
    await nextFrame()
    // Everything the painter is about to draw has to be ON HAND first: the engine's own material images are
    // only what the editor has had on screen, and a sprite that is missing is painted as nothing at all.
    await loadImagesForPicture(engine.pictureImageSources())
    const picture = engine.exportPicture(await loadTerrainImages(engine.pictureTerrainSources()))
    if (!picture) { state.hudStatus = 'Nothing to export: the track has no size yet.'; return }
    exporting.value = 'Writing the picture…'
    await nextFrame()
    const { blob, extension } = await encodePicture(picture.canvas)
    if (!blob) { state.hudStatus = 'Could not encode the picture.'; return }
    const pictureData = new Uint8Array(await blob.arrayBuffer())
    const { width, height } = picture.canvas
    exporting.value = 'Writing the card pictures…'
    await nextFrame()
    const previewData = await encodedCard(engine.exportPreviewPicture(picture.canvas))
    const blackData = await encodedCard(engine.exportBlackPicture())
    releaseCanvas(picture.canvas)

    const pictureFile = `${picture.track.id}.${extension}`
    const data = engine.exportTrackFile(pictureFile)
    const zip = makeZip([
      { path: `assets/tracks/${data.id}.json`, data: new TextEncoder().encode(JSON.stringify(data, null, 2)) },
      { path: `assets/img/tracks/${pictureFile}`, data: pictureData },
      { path: `assets/img/tracks/${tilePreviewPath(picture.track)}`, data: previewData },
      { path: `assets/img/tracks/${blackPicturePath(picture.track)}`, data: blackData },
    ])
    downloadBlob(`${data.id}.zip`, zip)
    state.hudStatus = `Exported ${data.id}.zip: picture (${width} × ${height} px, `
      + `${picture.scale.toFixed(2)} px per world unit) + preview + black + JSON `
      + `(${(zip.size / (1024 * 1024)).toFixed(1)} MB). Unzip it into the game's src/ folder.`
  } finally {
    engine.setBusy(false)
    exporting.value = null
  }
}

/**
 * Encodes one card picture and drops its canvas straight away: an export writes two of them, each a
 * canvas of its own.
 */
async function encodedCard(canvas) {
  if (!canvas) return null
  try { return await encodeWebp(canvas, 0.8) } finally { releaseCanvas(canvas) }
}

/**
 * The full package the game runs on: the JSON, the whole-map picture (the replay and the fallback)
 * and the streamed tile pyramid, in one ZIP laid out as the game's `src/` folder. The tiles are
 * painted in strips (see lib/trackEditor/tileExport.js): the export blocks the page for a minute or
 * two on the biggest tracks, which is why the overlay says which strip it is on.
 */
async function exportTiles() {
  if (exporting.value) return
  exporting.value = 'Painting the track…'
  engine.setBusy(true)
  try {
    await nextFrame()
    await loadImagesForPicture(engine.pictureImageSources())
    const terrains = await loadTerrainImages(engine.pictureTerrainSources())
    const track = engine.exportTrack()

    // The single picture first, and its backing store dropped straight after: the strips that come
    // next allocate tens of MB each, and this canvas can be 150 MB on its own.
    const picture = engine.exportPicture(terrains)
    if (!picture) { state.hudStatus = 'Nothing to export: the track has no size yet.'; return }
    exporting.value = 'Writing the picture…'
    await nextFrame()
    const { blob: pictureBlob, extension } = await encodePicture(picture.canvas)
    if (!pictureBlob) { state.hudStatus = 'Could not encode the picture.'; return }
    const pictureData = new Uint8Array(await pictureBlob.arrayBuffer())
    const blackData = await encodedCard(engine.exportBlackPicture())
    picture.canvas.width = 1
    picture.canvas.height = 1

    const tiles = await exportTrackTiles(track, {
      encode: encodeWebp,
      images: Object.fromEntries(engine.materialImages),
      terrainImages: terrains,
      onProgress: async ({ phase, index, total }) => {
        if (phase !== 'paint') return
        exporting.value = `Painting the tiles: strip ${index + 1} of ${total}…`
        await nextFrame()
      },
    })
    if (!tiles) { state.hudStatus = 'Nothing to export: the track has no size yet.'; return }

    exporting.value = 'Packing the archive…'
    await nextFrame()
    const pictureFile = `${track.id}.${extension}`
    const data = engine.exportTrackFile(pictureFile, tiles.manifest)
    const zip = makeZip([
      { path: `assets/tracks/${track.id}.json`, data: new TextEncoder().encode(JSON.stringify(data, null, 2)) },
      { path: `assets/img/tracks/${pictureFile}`, data: pictureData },
      { path: `assets/img/tracks/${blackPicturePath(track)}`, data: blackData },
      ...tiles.files.map((file) => ({ path: `assets/img/tracks/${file.path}`, data: file.data })),
    ])
    downloadBlob(`${track.id}-tiles.zip`, zip)
    state.hudStatus = `Exported ${track.id}-tiles.zip: ${tiles.tileCount} tiles + preview + black + picture + JSON `
      + `(${(zip.size / (1024 * 1024)).toFixed(1)} MB). Unzip it into the game's src/ folder.`
  } catch (err) {
    console.error(err)
    state.hudStatus = `Export failed: ${err?.message ?? err}`
  } finally {
    engine.setBusy(false)
    exporting.value = null
  }
}
/**
 * The ground textures the picture needs, fetched the same way the sprites are: a src that never arrives
 * leaves the painter on its own procedural look for that ground.
 */
async function loadTerrainImages(sources) {
  const images = {}
  await Promise.all(sources.map(({ terrain, src }) => new Promise((resolve) => {
    const img = new Image()
    img.onload = () => { images[terrain] = img; resolve() }
    img.onerror = resolve
    img.src = `/img/${src}`
  })))
  return images
}

/**
 * One picture, in the smallest file that keeps the painted lines crisp: WebP, and PNG where the browser
 * cannot write WebP (it says which it wrote by handing back that type).
 */
function encodePicture(canvas) {
  return new Promise((resolve) => {
    canvas.toBlob((webp) => {
      if (webp && webp.type === 'image/webp') return resolve({ blob: webp, extension: 'webp' })
      canvas.toBlob((png) => resolve({ blob: png, extension: 'png' }), 'image/png')
    }, 'image/webp', 0.92)
  })
}

/**
 * Writes the track as ONE PNG PER LAYER (see the engine): the road in black, the objects in red, the
 * starting grid and the finish line in light blue. All three are the same size, in the frame of the
 * game area and on a transparent background, so they can be finished one at a time and stacked back
 * into the single flat picture — the very rectangle the game stretches a background image over.
 */
async function exportPng() {
  if (exporting.value) return
  exporting.value = 'Painting the layers…'
  engine.setBusy(true)
  try {
    await nextFrame()
    const images = engine.exportImages()
    if (!images.length) { state.hudStatus = 'Nothing to export: the track has no size yet.'; return }
    for (const image of images) {
      image.canvas.toBlob((blob) => { if (blob) downloadBlob(`${image.id}_${image.name}.png`, blob) }, 'image/png')
    }
    // Set after the engine's own notify, so it is what the HUD shows until the next change.
    const first = images[0]
    state.hudStatus = `Exported ${images.length} PNGs (${images.map((i) => i.name).join(', ')} — ${first.width} × ${first.height} px`
      + (first.scale < 1 ? `, scaled to ${Math.round(first.scale * 100)}%)` : ')')
  } finally {
    engine.setBusy(false)
    exporting.value = null
  }
}

/**
 * The track's picture, painted the same way and at the same size every export writes it: saved as a
 * file and opened in a window of its own to be looked at.
 */
async function showPreview() {
  if (exporting.value) return
  // Opened here, while the click still counts as the gesture: the paint takes a while, and a window
  // opened after it is a popup the browser blocks.
  const previewWindow = window.open('', '_blank')
  exporting.value = 'Painting the track…'
  engine.setBusy(true)
  try {
    await nextFrame()
    await loadImagesForPicture(engine.pictureImageSources())
    const picture = engine.exportPicture(await loadTerrainImages(engine.pictureTerrainSources()))
    if (!picture) { state.hudStatus = 'Nothing to preview: the track has no size yet.'; return }
    exporting.value = 'Writing the picture…'
    await nextFrame()
    const { blob, extension } = await encodePicture(picture.canvas)
    if (!blob) { state.hudStatus = 'Could not encode the picture.'; return }
    const { width, height } = picture.canvas
    releaseCanvas(picture.canvas)
    downloadBlob(`${picture.track.id}.${extension}`, blob)
    if (previewWindow) previewWindow.location = URL.createObjectURL(blob)
    state.hudStatus = `Preview ${picture.track.id}.${extension}: ${width} × ${height} px, `
      + `${picture.scale.toFixed(2)} px per world unit`
  } catch (err) {
    console.error(err)
    state.hudStatus = `Preview failed: ${err?.message ?? err}`
  } finally {
    engine.setBusy(false)
    exporting.value = null
  }
}

onMounted(() => {
  engine = new TrackEditorEngine({
    onChange: handleChange,
    onCoords: (x, y) => { hudCoords.value = `${x}, ${y}` },
    onCursor: (c) => { cursor.value = c },
  })
  try {
    const savedLimit = localStorage.getItem(HISTORY_LIMIT_KEY)
    if (savedLimit !== null) engine.setHistoryLimit(savedLimit)
  } catch { /* Browser storage may be unavailable. */ }
  // Before mount: the very first render already draws whatever the imported track holds.
  engine.onImages = loadMaterialImages
  engine.mount(canvasEl.value, wrapperEl.value)
  handleChange(engine.getSnapshot())
})

onUnmounted(() => engine?.destroy())
</script>

<template>
  <div class="track-editor" :style="{ '--pose-anchor': POSE_ANCHOR_COLOR }" @focusin="beginFieldEdit" @focusout="endFieldEdit">
    <!-- The editor's own menu bar: the site's own bar is hidden on this route (see App.vue), and
         what used to be panels on the left (File, Clipboard, View, Controls) lives up here now. -->
    <div class="editor-menubar">
      <!-- File: everything that opens, reads or writes the track as a whole. -->
      <v-menu location="bottom start">
        <template #activator="{ props }">
          <button type="button" class="menubar-btn" v-bind="props">File</button>
        </template>
        <v-list density="compact" min-width="240">
          <v-list-item prepend-icon="mdi-file-plus-outline" title="New Track" @click="newTrack" />
          <v-list-item prepend-icon="mdi-folder-open-outline" title="Import JSON" @click="triggerImport" />
          <v-divider class="my-1" />
          <v-tooltip location="right" open-delay="400" text="One ZIP laid out as the game's src/ folder: the JSON that names the track's picture and stamps the geometry it was painted from, the picture its whole look is painted into (WebP, or PNG where the browser cannot write it), and the two pictures the in-game track picker can show on a card — preview/ and black/.">
            <template #activator="{ props: tip }">
              <v-list-item v-bind="tip" prepend-icon="mdi-content-save-outline" title="Export track (ZIP)" @click="exportTrack" />
            </template>
          </v-tooltip>
          <v-tooltip location="right" open-delay="400" text="Everything the game needs in one ZIP: the JSON, the whole-map picture (replay and fallback) and the tile pyramid the game streams at every zoom. Unzip it into the game's src/ folder — the paths inside are already assets/tracks/… and assets/img/tracks/…">
            <template #activator="{ props: tip }">
              <v-list-item v-bind="tip" prepend-icon="mdi-folder-zip-outline" title="Export track with tiles (ZIP)" @click="exportTiles" />
            </template>
          </v-tooltip>
          <v-tooltip location="right" open-delay="400" text="Export one PNG per layer — track.png, objects.png, lines.png — each on a transparent background: the road in black, the objects in red, the starting grid and the finish line in light blue, all in the frame of the game area.">
            <template #activator="{ props: tip }">
              <v-list-item v-bind="tip" prepend-icon="mdi-image-multiple-outline" title="Export layers to paint" @click="exportPng" />
            </template>
          </v-tooltip>
        </v-list>
      </v-menu>

      <!-- Edit: editing tools — clipboard, snapping and the geometry commands. -->
      <v-menu location="bottom start">
        <template #activator="{ props }">
          <button type="button" class="menubar-btn" v-bind="props">Edit</button>
        </template>
        <v-list density="compact" min-width="240">
          <v-list-item prepend-icon="mdi-undo" :title="'Undo (' + state.undoCount + ')'" :disabled="!state.canUndo" @click="undo">
            <template #append><span class="menubar-shortcut">Ctrl+Z</span></template>
          </v-list-item>
          <v-list-item prepend-icon="mdi-redo" :title="'Redo (' + state.redoCount + ')'" :disabled="!state.canRedo" @click="redo">
            <template #append><span class="menubar-shortcut">Ctrl+Shift+Z / Ctrl+Y</span></template>
          </v-list-item>
          <v-list-item prepend-icon="mdi-history" title="Undo / Redo Settings…" @click="openHistorySettings" />
          <v-divider class="my-1" />
          <v-list-item prepend-icon="mdi-content-copy" title="Copy" :disabled="!state.canCopy" @click="copySelected">
            <template #append><span class="menubar-shortcut">Ctrl+C</span></template>
          </v-list-item>
          <v-list-item prepend-icon="mdi-content-paste" title="Paste" :disabled="!state.hasClipboard" @click="pasteClipboard">
            <template #append><span class="menubar-shortcut">Ctrl+V</span></template>
          </v-list-item>
          <v-list-item prepend-icon="mdi-delete-outline" title="Delete Selection" :disabled="!state.selectionCount" @click="deleteSelected">
            <template #append><span class="menubar-shortcut">Del</span></template>
          </v-list-item>
          <v-divider class="my-1" />
          <v-tooltip location="right" open-delay="400" text="Flip the selected object horizontally, in place.">
            <template #activator="{ props: tip }">
              <v-list-item v-bind="tip" prepend-icon="mdi-flip-horizontal" title="Flip Horizontal" :disabled="!state.selection" @click="flipHorizontal" />
            </template>
          </v-tooltip>
          <v-tooltip location="right" open-delay="400" text="Magnet: while you place or drag a point, pull it onto a nearby node, onto the track's own edge or centre line (so a wall runs along the asphalt instead of next to it), or onto the alignment line where it lines up with another element's node.">
            <template #activator="{ props: tip }">
              <v-list-item v-bind="tip" prepend-icon="mdi-magnet" title="Magnetic Snap" @click="toggleSnap">
                <template #append><v-icon v-if="magneticSnap" size="small" color="primary">mdi-check</v-icon></template>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-divider class="my-1" />
          <v-tooltip location="right" open-delay="400" text="Recalculate the game area (Width × Height) so that everything on the map is inside it. The area starts at the world origin, so a track lying to the left of or above it is moved inside instead — every coordinate shifts, Undo restores the previous geometry and size.">
            <template #activator="{ props: tip }">
              <v-list-item v-bind="tip" prepend-icon="mdi-vector-square" title="Fit Game Area" @click="fitGameArea" />
            </template>
          </v-tooltip>
        </v-list>
      </v-menu>

      <!-- View: what the canvas shows. -->
      <v-menu location="bottom start">
        <template #activator="{ props }">
          <button type="button" class="menubar-btn" v-bind="props">View</button>
        </template>
        <v-list density="compact" min-width="240">
          <v-list-item prepend-icon="mdi-crop-free" title="Fit to View" @click="fitView" />
          <v-tooltip location="right" open-delay="400" text="Fade the track and the objects to near-transparent to see the background image.">
            <template #activator="{ props: tip }">
              <v-list-item v-bind="tip" prepend-icon="mdi-eye-off-outline" title="Hide Objects" @click="toggleHideObjects">
                <template #append><v-icon v-if="hideObjects" size="small" color="primary">mdi-check</v-icon></template>
              </v-list-item>
            </template>
          </v-tooltip>
          <v-divider class="my-1" />
          <v-tooltip location="right" open-delay="400" text="Paint the track's picture exactly as an export writes it — the whole map at its own resolution — save it, and open it in a window of its own.">
            <template #activator="{ props: tip }">
              <v-list-item v-bind="tip" prepend-icon="mdi-image-outline" title="Preview" @click="showPreview" />
            </template>
          </v-tooltip>
        </v-list>
      </v-menu>

      <!-- Tools: what finishes a track outside the editor. -->
      <v-menu location="bottom start">
        <template #activator="{ props }">
          <button type="button" class="menubar-btn" v-bind="props">Tools</button>
        </template>
        <v-list density="compact" min-width="240">
          <v-list-item
            :to="{ name: 'post_production' }"
            prepend-icon="mdi-layers-triple-outline"
            title="Post production"
          />
        </v-list>
      </v-menu>

      <!-- Help: how to use the editor. -->
      <v-menu location="bottom start">
        <template #activator="{ props }">
          <button type="button" class="menubar-btn" v-bind="props">Help</button>
        </template>
        <v-list density="compact" min-width="240">
          <v-list-item prepend-icon="mdi-keyboard-outline" title="Controls" @click="controlsDialog = true" />
        </v-list>
      </v-menu>
    </div>

    <!-- The Import JSON entry of the File menu clicks this. -->
    <input type="file" ref="fileInputEl" accept=".json" style="display:none" @change="onImportFile">

    <div id="sidebar" class="editor-sidebar">
      <div class="panel editor-actions" role="group" aria-label="Editor actions">
        <button type="button" class="btn editor-action" :class="{ active: state.mode === 'select' }"
          :aria-pressed="state.mode === 'select'" aria-label="Select" title="Selection mode" @click="selectMode">
          <v-icon icon="mdi-cursor-default" size="20" aria-hidden="true" />
        </button>
        <button type="button" class="btn editor-action" :disabled="!state.canUndo"
          aria-label="Undo" title="Undo (Ctrl+Z)" @click="undo">
          <v-icon icon="mdi-undo" size="20" aria-hidden="true" />
        </button>
        <button type="button" class="btn editor-action" :disabled="!state.canRedo"
          aria-label="Redo" title="Redo (Ctrl+Shift+Z / Ctrl+Y)" @click="redo">
          <v-icon icon="mdi-redo" size="20" aria-hidden="true" />
        </button>
      </div>
      <!-- Build -->
      <div class="panel">
        <div class="panel-title">Build</div>
        <button class="btn" :class="{ active: state.mode === 'addSeg' }" title="Add a road segment (2 clicks)" @click="setMode('addSeg')">
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="0.75" y="0.75" width="12.5" height="8.5" rx="1" />
          </svg>
          Segment
        </button>
        <button class="btn" :class="{ active: state.mode === 'addRoad' }" title="Draw a road AREA: click around the perimeter of the road (its outer edge, then back along the inner edge), double-click / Enter to finish — the shape closes itself back on the first node. Its interior is asphalt for the game. In Select mode drag a node to move it, drag its handles (circles beside the selected node) to curve the edges, double-click an edge to add a node there, a node to delete it." @click="setMode('addRoad')">
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none" stroke="currentColor" stroke-width="1.4">
            <path d="M1.5 8.5 C4 8.5 3.2 1.5 7 1.5 C10.8 1.5 10 8.5 12.5 8.5" />
            <line x1="1.5" y1="8.5" x2="12.5" y2="8.5" stroke-dasharray="2 2" />
          </svg>
          Road Area
        </button>
        <button class="btn" :class="{ active: state.mode === 'addCp' }" title="Add a checkpoint line (2 clicks)" @click="setMode('addCp')">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5">
            <line x1="7" y1="1" x2="7" y2="13" />
            <line x1="1" y1="7" x2="13" y2="7" />
          </svg>
          Checkpoint
        </button>
        <button class="btn" :class="{ active: state.mode === 'addSg' }" title="Place the starting grid line (2 clicks, only one allowed)" @click="selectStartingGrid">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5">
            <line x1="1" y1="7" x2="13" y2="7" />
            <polyline points="10,4 13,7 10,10" />
          </svg>
          Starting Grid
        </button>
        <button class="btn" :class="{ active: state.mode === 'addPit' }" title="Place the pit box rectangle (2 clicks to define opposite corners, only one allowed)" @click="selectPitBox">
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="1" y="1" width="12" height="8" rx="1" fill="currentColor" fill-opacity="0.15" />
          </svg>
          Pit Box
        </button>
      </div>

      <!-- Obstacles -->
      <div class="panel">
        <div class="panel-title">Obstacles (walls)</div>
        <button class="btn" :class="{ active: state.mode === 'addWall' }" title="Add a wall line, stretchable/movable with configurable thickness (2 clicks)" @click="setMode('addWall')">
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none" stroke="#dc2626" stroke-width="2.5">
            <line x1="1" y1="9" x2="13" y2="1" />
          </svg>
          Wall
        </button>
        <button class="btn" :class="{ active: state.mode === 'addCircle' }" title="Add a circular obstacle (click center, then edge)" @click="setMode('addCircle')">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#dc2626" stroke-width="1.5">
            <circle cx="7" cy="7" r="6" fill="#dc2626" fill-opacity="0.2" />
          </svg>
          Circle
        </button>
        <button class="btn" :class="{ active: state.mode === 'addRect' }" title="Add a rectangular obstacle (2 clicks to define opposite corners)" @click="setMode('addRect')">
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none" stroke="#dc2626" stroke-width="1.5">
            <rect x="1" y="1" width="12" height="8" rx="1" fill="#dc2626" fill-opacity="0.2" />
          </svg>
          Rectangle
        </button>
        <button class="btn" :class="{ active: state.mode === 'addPoly' }" title="Draw a polygon wall: click each node, then double-click / Enter / right-click to finish (Esc cancels). Click an end node of an existing polygon to continue it; double-click a node to delete it." @click="setMode('addPoly')">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#dc2626" stroke-width="1.5">
            <polyline points="1,11 5,2 12,5 8,12" />
          </svg>
          Polygon
        </button>
      </div>

      <!-- Terrain -->
      <div class="panel">
        <div class="panel-title">Terrain (ground)</div>
        <button class="btn" :class="{ active: state.mode === 'addTerrainRect' }" title="Add a terrain rectangle (2 clicks to define opposite corners)" @click="setMode('addTerrainRect')">
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none" stroke="#a9793f" stroke-width="1.5">
            <rect x="1" y="1" width="12" height="8" rx="1" fill="#a9793f" fill-opacity="0.25" />
          </svg>
          Terrain Rect
        </button>
        <button class="btn" :class="{ active: state.mode === 'addTerrainCircle' }" title="Add a terrain circle (click center, then edge)" @click="setMode('addTerrainCircle')">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#a9793f" stroke-width="1.5">
            <circle cx="7" cy="7" r="6" fill="#a9793f" fill-opacity="0.25" />
          </svg>
          Terrain Circle
        </button>
        <button class="btn" :class="{ active: state.mode === 'addTerrainArea' }" title="Draw a terrain area (click each node, double-click / Enter to finish)" @click="setMode('addTerrainArea')">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#a9793f" stroke-width="1.4">
            <polyline points="1,11 5,2 12,5 8,12" />
          </svg>
          Terrain Area
        </button>
      </div>

      <div class="panel sprite-palette">
        <div class="panel-title">Sprite palette</div>
        <div class="palette-tabs" role="group" aria-label="Sprite material">
          <button v-for="material in ['houses', 'signals']" :key="material" type="button"
            class="btn" :class="{ active: paletteMaterial === material }"
            :aria-pressed="paletteMaterial === material" @click="paletteMaterial = material">
            {{ material === 'houses' ? 'Houses' : 'Signals' }}
          </button>
        </div>
        <p class="hint">Drag a sprite onto the canvas.</p>
        <div class="palette-grid" :aria-label="paletteMaterial + ' sprites'">
          <div v-for="(variant, index) in paletteVariants" :key="variant.src" class="palette-sprite"
            draggable="true" :title="'Drag ' + paletteMaterial + ' — ' + variant.name"
            @dragstart="startSpriteDrag($event, index)" @dragend="paletteDrag = false">
            <img :src="'/img/' + variant.src" :alt="paletteMaterial + ' — ' + variant.name" draggable="false">
            <span>{{ variant.name }}</span>
          </div>
        </div>
      </div>

      <!-- Defaults -->
      <div class="panel">
        <div class="panel-title collapsible" @click="toggleCollapse('defaults')">
          Defaults
          <span class="collapse-arrow" :class="{ collapsed: collapsed.defaults }">▾</span>
        </div>
        <div v-show="!collapsed.defaults">
        <div class="form-row">
          <label>Segment width (world units)</label>
          <input type="number" v-model.number="defWidth" min="20" max="800" step="10">
        </div>
        <div class="form-row">
          <label>Default lane</label>
          <select v-model="defLane">
            <option value="main">main</option>
            <option value="pit">pit</option>
          </select>
        </div>
        <div class="form-row">
          <label>Wall thickness (world units)</label>
          <input type="number" v-model.number="defObsThickness" min="2" max="400" step="2">
        </div>
        <div class="form-row">
          <label>Polygon wall thickness (world units)</label>
          <input type="number" v-model.number="defPolyThickness" min="2" max="400" step="2">
        </div>
        <div class="form-row">
          <label>Obstacle bounciness (0–1, low = wall-like)</label>
          <input type="number" v-model.number="defObsRestitution" min="0" max="1" step="0.05">
        </div>
        <div class="form-row">
          <label>Obstacle friction (px/s² deceleration while touching)</label>
          <input type="number" v-model.number="defObsFriction" min="0" step="50">
        </div>
        <div class="form-row">
          <label>Obstacle material (impact sound)</label>
          <select v-model="defObsMaterial">
            <option v-for="m in materialOptions" :key="m" :value="m">{{ MATERIALS[m]?.label ?? m }}</option>
          </select>
        </div>
        <div class="form-row">
          <label>Terrain of new figures</label>
          <TerrainTexturePicker v-model="defTerrain" :groups="TERRAIN_GROUPS" />
        </div>
        <div class="form-row">
          <label>Ground (default terrain)</label>
          <TerrainTexturePicker v-model="groundTerrain" :groups="TERRAIN_GROUPS" />
          <span class="hint">The whole map outside the road and the terrain figures; the cars drive on it too.</span>
        </div>
        <div class="form-row">
          <label><input type="checkbox" v-model="trees"> Trees around the map</label>
          <span class="hint">Inside the map and in the band outside it. The woodland along the edge has its own switch.</span>
        </div>
        <div class="form-row">
          <label><input type="checkbox" v-model="borderTrees"> Wooded border</label>
          <span class="hint">Draws the woodland around the map; the map's edge is solid either way.</span>
        </div>
        </div>
      </div>

      <!-- Track info -->
      <div class="panel">
        <div class="panel-title collapsible" @click="toggleCollapse('trackInfo')">
          Track Info
          <span class="collapse-arrow" :class="{ collapsed: collapsed.trackInfo }">▾</span>
        </div>
        <div v-show="!collapsed.trackInfo">
        <div class="form-row">
          <label>ID</label>
          <input type="text" v-model="trackId" placeholder="track_custom">
        </div>
        <div class="form-row">
          <label>Name</label>
          <input type="text" v-model="trackName" placeholder="Custom Track">
        </div>
        <div class="form-row">
          <label>Width (world units, blank = auto)</label>
          <input type="number" v-model="trackWidth" min="100" step="50" placeholder="auto">
        </div>
        <div class="form-row">
          <label>Height (world units, blank = auto)</label>
          <input type="number" v-model="trackHeight" min="100" step="50" placeholder="auto">
        </div>
        </div>
      </div>

    </div>

    <div id="canvas-wrapper" ref="wrapperEl" :class="{ 'sprite-drop-active': paletteDrag }"
      @dragover="overSpriteCanvas" @dragleave.self="paletteDrag = false" @drop="dropSprite">
      <canvas id="canvas" ref="canvasEl" :style="{ cursor }"></canvas>
      <div class="hud" id="hud-status">{{ state.hudStatus }}</div>
      <div class="hud" id="hud-coords">{{ hudCoords }}</div>
    </div>

    <aside id="properties-sidebar" class="editor-sidebar" aria-label="Selection properties">
      <nav class="scene-tree" aria-label="Track elements" @mouseleave="hoverTreeItem(null)">
        <div v-for="group in state.objectTree" :key="group.key" class="tree-group" :style="{ '--tree-color': group.color }">
          <button type="button" class="tree-row tree-category" :aria-expanded="treeExpanded[group.key]"
            :aria-controls="'tree-' + group.key" @click="treeExpanded[group.key] = !treeExpanded[group.key]"
            @mouseenter="hoverTreeItem(null)">
            <span class="tree-arrow" aria-hidden="true">{{ treeExpanded[group.key] ? '▾' : '▸' }}</span>
            <span>{{ group.label }}</span>
            <span class="tree-count">{{ group.children.length }}</span>
          </button>
          <ul v-show="treeExpanded[group.key]" :id="'tree-' + group.key" class="tree-children">
            <li v-for="item in group.children" :key="item.key">
              <button type="button" class="tree-row tree-item" :class="{ selected: isTreeSelected(item) }"
                :aria-pressed="isTreeSelected(item)" @mouseenter="hoverTreeItem(item)"
                @mouseleave="hoverTreeItem(null)" @click="selectTreeItem(item)">
                {{ item.label }}
              </button>
            </li>
          </ul>
        </div>
      </nav>
      <!-- Properties -->
      <div class="panel">
        <div class="panel-title collapsible" @click="toggleCollapse('properties')">
          Properties
          <span class="collapse-arrow" :class="{ collapsed: collapsed.properties }">▾</span>
        </div>
        <div v-show="!collapsed.properties">
        <div v-if="!state.selection"><span class="hint">Nothing selected</span></div>
        <div v-if="state.selectionCount > 1" class="multi-hint">
          {{ state.selectionCount }} elements selected — a field you change here applies to every selected element of that type.
        </div>

        <div v-if="canEditPose">
          <div class="form-row">
            <label>Position <span class="anchor-dot" aria-hidden="true"></span></label>
            <div class="field-pair">
              <input type="number" v-model.number="poseX" step="1" title="X" aria-label="X">
              <input type="number" v-model.number="poseY" step="1" title="Y" aria-label="Y">
            </div>
            <span class="hint">X/Y of the green point, where the element starts being drawn: it moves the element whole.</span>
          </div>
          <div v-if="state.selection.angleDeg !== undefined" class="form-row">
            <label>Angle (degrees)</label>
            <input type="number" v-model.number="poseAngleDeg" step="1">
            <span class="hint">Direction the element leaves the green point in: 0° is right, growing clockwise on screen. It turns around that point.</span>
          </div>
          <div class="divider"></div>
        </div>

        <div v-if="state.selection?.type === 'seg'">
          <div class="prop-name">Segment #{{ state.selection.id }}</div>
          <div class="form-row">
            <label>Width</label>
            <input type="number" v-model.number="segWidth" min="20" max="800" step="10">
          </div>
          <div class="form-row">
            <label><input type="checkbox" v-model="segPit"> Pit lane (yellow line, pit logic in game)</label>
          </div>
          <div class="form-row">
            <label>Surface</label>
            <select v-model="segSurface">
              <option v-for="s in surfaceOptions" :key="s" :value="s">{{ s.toUpperCase() }}</option>
            </select>
          </div>
          <div class="divider"></div>
          <button v-if="!state.selection.hasCurve" class="btn" @click="curveSeg">⌒&nbsp; Make Curve</button>
          <button v-else class="btn" @click="straightenSeg">↔&nbsp; Straighten</button>
          <button class="btn btn-danger" @click="deleteSelected">✕&nbsp; Delete Segment</button>
        </div>

        <div v-if="state.selection?.type === 'cp'">
          <div class="prop-name">{{ state.selection.isStart ? 'Start / Finish Line' : `Checkpoint #${state.selection.id}` }}</div>
          <div class="form-row" v-if="state.selection.isStart">
            <span class="hint">Always a timing point: sectors are measured from here.</span>
          </div>
          <div class="form-row" v-else>
            <label>
              <input type="checkbox" v-model="cpTiming">
              Timing point (sector split)
            </label>
          </div>
          <div class="divider"></div>
          <button class="btn btn-danger" @click="deleteSelected">✕&nbsp; Delete Checkpoint</button>
        </div>

        <div v-if="state.selection?.type === 'sg'">
          <div class="prop-name">Starting Grid</div>
          <div class="form-row">
            <label>Row stagger (px, better position leads)</label>
            <input type="number" v-model.number="sgStaggerPx" min="0" step="5">
          </div>
          <div class="divider"></div>
          <button class="btn" @click="flipDir">⇄&nbsp; Flip Direction</button>
          <button class="btn" @click="toggleSgFlip">⇄&nbsp; Flip Leading Side ({{ state.selection.flip ? 'right' : 'left' }})</button>
          <button class="btn btn-danger" @click="deleteSelected">✕&nbsp; Delete Grid</button>
        </div>

        <div v-if="state.selection?.type === 'pit'">
          <div class="prop-name">Pit Box</div>
          <div class="form-row">
            <label>Crew comes out of</label>
            <select v-model="pitCrewSide">
              <option value="auto">Auto (from how the car is parked)</option>
              <option value="left">Left edge</option>
              <option value="right">Right edge</option>
              <option value="top">Top edge</option>
              <option value="bottom">Bottom edge</option>
            </select>
            <span class="hint">Edge of the box the pit crew runs out of: the left/right edges line the mechanics up with the car's height, the top/bottom ones with its position across.</span>
          </div>
          <div class="form-row">
            <label>Crew line offset (px)</label>
            <input type="number" v-model.number="pitCrewOffset" min="0" max="400" step="4">
            <span class="hint">How far outside the box the crew appears — the red dashed line in the canvas.</span>
          </div>
          <div class="form-row">
            <label>Cars face</label>
            <select v-model="pitDirection">
              <option value="auto">Auto (from the lap)</option>
              <option value="forward">Forward (as the pit lane is drawn)</option>
              <option value="backward">Backward</option>
              <option value="angle">Angle…</option>
            </select>
            <input v-if="pitDirection === 'angle'" type="number" v-model.number="pitDirectionDeg" step="5">
            <span class="hint">Which way the cars parked here look. Auto is what the game works out on its own: the pit lane's direction, turned to the sense the lap is driven in (the checkpoints' order), whatever sense the lane is drawn in. Forward/Backward force one of the two — the white arrow in the box is what the game will use.</span>
          </div>
          <div class="divider"></div>
          <button class="btn btn-danger" @click="deleteSelected">✕&nbsp; Delete Pit Box</button>
        </div>

        <div v-if="OBSTACLE_TYPES.includes(state.selection?.type) || state.selection?.type === 'terrain'">
          <div class="form-row">
            <label><input type="checkbox" v-model="decorFlag"> Only decoration</label>
            <span class="hint">Drawn into the exported picture; not written to the track file, so the game never sees it.</span>
            <span v-if="state.selection?.type === 'terrain' && decorFlag" class="hint">A decorated terrain is painted only: in game this ground has no physics (no slowdown, no debris). Untick it to keep the terrain type.</span>
          </div>
          <div class="form-row">
            <label>Z-index</label>
            <input type="number" v-model.number="zIndex" step="1" placeholder="0">
            <span class="hint">Painting layer: a higher number lies over a lower one (empty = 0). Terrain figures order among themselves, obstacles among themselves.</span>
          </div>
          <div class="divider"></div>
        </div>

        <div v-if="state.selection?.type === 'wall'">
          <div class="prop-name">Wall #{{ state.selection.id }}</div>
          <div class="form-row">
            <label>Thickness</label>
            <input type="number" v-model.number="wallThickness" min="2" max="400" step="2">
          </div>
          <div v-if="!decorFlag" class="form-row">
            <label>Bounciness (0–1)</label>
            <input type="number" v-model.number="obsRestitution" min="0" max="1" step="0.05">
          </div>
          <div v-if="!decorFlag" class="form-row">
            <label>Friction (px/s² deceleration while touching)</label>
            <input type="number" v-model.number="obsFriction" min="0" step="50">
          </div>
          <div class="form-row">
            <label>{{ decorFlag ? 'Texture' : 'Material (impact sound)' }}</label>
            <select v-model="obsMaterial">
              <option v-for="m in obstacleMaterialOptions" :key="m" :value="m">{{ MATERIALS[m]?.label ?? m }}</option>
            </select>
          </div>
          <div class="divider"></div>
          <button class="btn btn-danger" @click="deleteSelected">✕&nbsp; Delete Wall</button>
        </div>

        <div v-if="state.selection?.type === 'circle'">
          <div class="prop-name">Circle #{{ state.selection.id }}</div>
          <div class="form-row">
            <label>Radius</label>
            <input type="number" v-model.number="circleRadius" min="5" step="5">
          </div>
          <div v-if="!decorFlag" class="form-row">
            <label>Bounciness (0–1)</label>
            <input type="number" v-model.number="obsRestitution" min="0" max="1" step="0.05">
          </div>
          <div v-if="!decorFlag" class="form-row">
            <label>Friction (px/s² deceleration while touching)</label>
            <input type="number" v-model.number="obsFriction" min="0" step="50">
          </div>
          <div class="form-row">
            <label>{{ decorFlag ? 'Texture' : 'Material (impact sound)' }}</label>
            <select v-model="obsMaterial">
              <option v-for="m in obstacleMaterialOptions" :key="m" :value="m">{{ MATERIALS[m]?.label ?? m }}</option>
            </select>
          </div>
          <div class="divider"></div>
          <button class="btn btn-danger" @click="deleteSelected">✕&nbsp; Delete Circle</button>
        </div>

        <div v-if="state.selection?.type === 'rect'">
          <div class="prop-name">Rectangle #{{ state.selection.id }}</div>
          <div class="form-row">
            <label>Width</label>
            <input type="number" v-model.number="rectWidth" min="10" step="5">
          </div>
          <div class="form-row">
            <label>Height</label>
            <input type="number" v-model.number="rectHeight" min="10" step="5">
          </div>
          <div class="form-row">
            <label>Angle (degrees)</label>
            <input type="number" v-model.number="rectAngle" step="5">
          </div>
          <span class="hint">Drag the green knob over the edge to turn it. A stand keeps its sprite's proportions, so its two sides always move together.</span>
          <div v-if="!decorFlag" class="form-row">
            <label>Bounciness (0–1)</label>
            <input type="number" v-model.number="obsRestitution" min="0" max="1" step="0.05">
          </div>
          <div v-if="!decorFlag" class="form-row">
            <label>Friction (px/s² deceleration while touching)</label>
            <input type="number" v-model.number="obsFriction" min="0" step="50">
          </div>
          <div class="form-row">
            <label>{{ decorFlag ? 'Texture' : 'Material (impact sound)' }}</label>
            <select v-model="obsMaterial">
              <option v-for="m in obstacleMaterialOptions" :key="m" :value="m">{{ MATERIALS[m]?.label ?? m }}</option>
            </select>
          </div>
          <div v-if="variantOptions.length" class="form-row">
            <label>Variant</label>
            <select v-model.number="obsVariant">
              <option v-for="(v, i) in variantOptions" :key="v.src" :value="i">{{ v.name }}</option>
            </select>
          </div>
          <img v-if="variantOptions.length" class="variant-preview" :src="variantPreview" alt="Variant">
          <div class="divider"></div>
          <button class="btn btn-danger" @click="deleteSelected">✕&nbsp; Delete Rectangle</button>
        </div>

        <div v-if="state.selection?.type === 'road'">
          <div class="prop-name">Road Area #{{ state.selection.id }} — {{ state.selection.nodes }} nodes{{ state.selection.curved ? `, ${state.selection.curved} curved` : '' }}</div>
          <div class="form-row">
            <label><input type="checkbox" v-model="areaPit"> Pit lane (yellow line, pit logic in game)</label>
          </div>
          <div class="form-row">
            <label>Surface</label>
            <select v-model="areaSurface">
              <option v-for="s in surfaceOptions" :key="s" :value="s">{{ s.toUpperCase() }}</option>
            </select>
          </div>
          <div class="divider"></div>
          <button class="btn" :disabled="state.selection.node == null || state.selection.nodeIsSmoothed" title="Round the two edges that meet at the selected node, so the outline passes smoothly through it: handles are laid along the circle through the node and the two beside it. Greys out once the node is already on that curve. Use Curve Whole Shape to do every node at once." @click="curveNode">⌒&nbsp; Curve Node</button>
          <button class="btn" title="Curve every node at once: a few nodes placed along a bend become that bend. It rounds the corners too, so a corner you want to keep is better served by Curve Node on the bend's own nodes." @click="curveAllNodes">⌒&nbsp; Curve Whole Shape</button>
          <button class="btn" :disabled="!state.selection.nodeHasHandles" @click="straightenNode">↔&nbsp; Straighten Node</button>
          <button class="btn btn-danger" @click="deleteSelected">✕&nbsp; Delete Road Area</button>
        </div>

        <div v-if="state.selection?.type === 'terrain'">
          <div class="prop-name">Terrain Area #{{ state.selection.id }} — {{ state.selection.nodes }} nodes{{ state.selection.curved ? `, ${state.selection.curved} curved` : '' }}</div>
          <div class="form-row">
            <label>{{ decorFlag ? 'Texture' : 'Terrain' }}</label>
            <TerrainTexturePicker v-model="terrainValue" :groups="TERRAIN_GROUPS" />
          </div>
          <div class="form-row">
            <label><input type="checkbox" v-model="terrainBlend"> Blend edges</label>
            <span class="hint">The area fades into the ground it borders instead of ending on its outline. On by default for loose ground (dirt, sand); off, the area keeps its outline drawn — a darker rim of its own ground and a soft shadow on the ground around it.</span>
          </div>
          <div class="divider"></div>
          <button class="btn" :disabled="state.selection.node == null || state.selection.nodeIsSmoothed" title="Round the two edges that meet at the selected node, so the outline passes smoothly through it." @click="curveNode">⌒&nbsp; Curve Node</button>
          <button class="btn" title="Curve every node at once: a few nodes placed along a bend become that bend." @click="curveAllNodes">⌒&nbsp; Curve Whole Shape</button>
          <button class="btn" :disabled="!state.selection.nodeHasHandles" @click="straightenNode">↔&nbsp; Straighten Node</button>
          <button class="btn btn-danger" @click="deleteSelected">✕&nbsp; Delete Terrain Area</button>
        </div>

        <div v-if="state.selection?.type === 'poly'">
          <div class="prop-name">Polygon #{{ state.selection.id }} — {{ state.selection.nodes }} nodes{{ state.selection.curved ? `, ${state.selection.curved} curved` : '' }}</div>
          <div class="form-row">
            <label>Wall thickness</label>
            <input type="number" v-model.number="polyThickness" min="2" max="400" step="2">
          </div>
          <div v-if="!decorFlag" class="form-row">
            <label>Bounciness (0–1)</label>
            <input type="number" v-model.number="obsRestitution" min="0" max="1" step="0.05">
          </div>
          <div v-if="!decorFlag" class="form-row">
            <label>Friction (px/s² deceleration while touching)</label>
            <input type="number" v-model.number="obsFriction" min="0" step="50">
          </div>
          <div class="form-row">
            <label>{{ decorFlag ? 'Texture' : 'Material (impact sound)' }}</label>
            <select v-model="obsMaterial">
              <option v-for="m in obstacleMaterialOptions" :key="m" :value="m">{{ MATERIALS[m]?.label ?? m }}</option>
            </select>
          </div>
          <span class="hint">Drag a node to move it; drag its handles to curve the edges (Alt = independent). Double-click an edge to add a node, a node to remove it.</span>
          <div class="divider"></div>
          <button class="btn" :disabled="state.selection.node == null || state.selection.nodeIsSmoothed" title="Round the two edges that meet at the selected node, so the outline passes smoothly through it: handles are laid along the circle through the node and the two beside it. Greys out once the node is already on that curve. Use Curve Whole Shape to do every node at once." @click="curveNode">⌒&nbsp; Curve Node</button>
          <button class="btn" title="Curve every node at once: a few nodes placed along a bend become that bend. It rounds the corners too, so a corner you want to keep is better served by Curve Node on the bend's own nodes." @click="curveAllNodes">⌒&nbsp; Curve Whole Shape</button>
          <button class="btn" :disabled="!state.selection.nodeHasHandles" @click="straightenNode">↔&nbsp; Straighten Node</button>
          <button class="btn btn-danger" @click="deleteSelected">✕&nbsp; Delete Polygon</button>
        </div>
        </div>
      </div>

    </aside>

    <v-dialog v-model="historyDialog" max-width="420">
      <v-card>
        <v-card-title class="text-body-1 font-weight-bold">Undo / Redo Settings</v-card-title>
        <v-card-text>
          <v-text-field v-model.number="historyLimitDraft" label="Maximum undo steps" type="number" min="1" max="1000" step="1"
            hint="Default: 20. Lowering the limit discards the oldest stored steps." persistent-hint />
          <p class="mt-4 text-body-2">The limit is remembered in this browser. Edit history lasts until you leave or reload the editor.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="historyDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="text" :disabled="!Number.isInteger(historyLimitDraft) || historyLimitDraft < 1 || historyLimitDraft > 1000" @click="saveHistorySettings">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Info ▸ Controls -->
    <v-dialog v-model="controlsDialog" max-width="460">
      <v-card>
        <v-card-title class="text-body-1 font-weight-bold">Controls</v-card-title>
        <v-card-text>
          <div class="controls-grid">
            <span>Ctrl+Z</span><span>Undo</span>
            <span>Ctrl+Shift+Z / Ctrl+Y</span><span>Redo</span>
            <span>Scroll wheel</span><span>Zoom</span>
            <span>Middle-drag</span><span>Pan</span>
            <span>Drag on empty space</span><span>Select every element the box touches</span>
            <span>Shift / Ctrl + click</span><span>Add or remove an element from the selection</span>
            <span>Drag a selected element</span><span>Move the whole selection</span>
            <span>Delete / Backspace</span><span>Remove the selection</span>
            <span>Escape</span><span>Cancel / deselect</span>
            <span>Polygon — double-click, Enter or right-click</span><span>Finish drawing</span>
            <span>Polygon — click an end node</span><span>Continue that polygon</span>
            <span>Polygon — double-click a node</span><span>Delete that node</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="controlsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Exporting paints the whole map in one synchronous call, so the page freezes for as long as it
         takes: this is what says which of the two steps it is on. It covers the editor whole because the
         export has to be left alone — an edit now would end up in the JSON of a picture painted before it. -->
    <div v-if="exporting" class="export-overlay">
      <div class="export-box">
        <span class="export-spinner" aria-hidden="true"></span>
        <span>{{ exporting }}</span>
        <span class="export-hint">A large track takes a while.</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.track-editor {
  --he-panel-bg: #161b22;
  display: flex;
  /* Full viewport: on this route the site's own bar and footer are hidden (see App.vue), and the
     40px of padding leaves room for the editor's fixed menu bar above. */
  height: 100dvh;
  padding-top: 40px;
  box-sizing: border-box;
  min-height: 600px;
  overflow: hidden;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  font-size: 13px;
  user-select: none;
  background: #0d1117;
  color: #c9d1d9;
}

/* The editor's own menu bar, pinned to the top of the viewport. */
.editor-menubar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 2px;
  height: 40px;
  padding: 0 8px;
  background: #161b22;
  border-bottom: 1px solid #30363d;
  box-sizing: border-box;
}

.menubar-btn {
  height: 30px;
  padding: 0 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #c9d1d9;
  font-family: inherit;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
}
.menubar-btn:hover { background: #2d333b; }

/* Keyboard shortcut shown on the right of a menu entry. */
.menubar-shortcut {
  font-size: 11px;
  color: #6e7681;
  font-variant-numeric: tabular-nums;
}

/* Info ▸ Controls cheatsheet. */
.controls-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 18px;
  font-size: 13px;
}
.controls-grid span:nth-child(odd) { color: #8b949e; white-space: nowrap; }

.editor-sidebar {
  width: 220px;
  flex-shrink: 0;
  background: var(--he-panel-bg);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
}

#sidebar { border-right: 1px solid #30363d; }

#properties-sidebar {
  width: 280px;
  border-left: 1px solid #30363d;
}

.scene-tree {
  background: #000;
  padding: 7px;
  border-bottom: 1px solid #30363d;
  max-height: 40dvh;
  flex-shrink: 0;
  overflow-y: auto;
}

.tree-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 7px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: var(--tree-color);
  font: inherit;
  text-align: left;
  cursor: pointer;
  overflow-wrap: anywhere;
}
.tree-row:hover { background: #161b22; }
.tree-row:focus-visible { outline: 1px solid var(--tree-color); outline-offset: -1px; }
.tree-category { font-weight: 600; }
.tree-arrow { width: 12px; flex-shrink: 0; }
.tree-count { margin-left: auto; font-size: 11px; opacity: 0.7; }
.tree-children { list-style: none; margin: 0; padding: 0 0 0 18px; }
.tree-item { font-size: 12px; }
.tree-item.selected { background: #161b22; border-color: var(--tree-color); }
.panel {
  padding: 7px;
  border-bottom: 1px solid #21262d;
  flex-shrink: 0;
}

.panel-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #6e7681;
  margin-bottom: 10px;
  background: #000;
  padding: 7px;
}
.panel-title.collapsible { cursor: pointer; }

.collapse-arrow {
  transition: transform 0.15s;
}
.collapse-arrow.collapsed { transform: rotate(-90deg); }

.btn {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  padding: 7px 10px;
  margin-bottom: 5px;
  background: #21262d;
  border: 1px solid #30363d;
  color: #c9d1d9;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  text-align: left;
  font-family: inherit;
  transition: background 0.1s;
  line-height: 1;
}
.editor-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
}
.btn.editor-action {
  justify-content: center;
  min-height: 36px;
  padding: 7px;
  margin-bottom: 0;
}
.editor-action:focus-visible { outline: 2px solid #388bfd; outline-offset: 2px; }
.btn:last-child { margin-bottom: 0; }
.btn:hover { background: #2d333b; border-color: #6e7681; }
.btn.active { background: #1c2d5a; border-color: #388bfd; color: #a5c8ff; }
.btn-danger { color: #f85149; border-color: #4a1515; background: #200d0d; }
.btn-danger:hover { background: #c0392b; border-color: #f85149; color: #fff; }
.btn:disabled { opacity: 0.4; cursor: not-allowed; }
.btn:disabled:hover { background: #21262d; border-color: #30363d; }

.form-row { margin-bottom: 8px; }
.form-row:last-child { margin-bottom: 0; }

/* Two fields side by side: the X/Y pair of the selected element. */
.field-pair { display: flex; gap: 6px; }

/* The point the Position fields place, at the same colour the canvas draws it in. */
.anchor-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-left: 4px;
  border-radius: 1px;
  background: var(--pose-anchor);
  vertical-align: middle;
}

label {
  display: block;
  font-size: 11px;
  color: #8b949e;
  margin-bottom: 3px;
}

input[type="number"],
input[type="text"] {
  width: 100%;
  padding: 5px 8px;
  background: #0d1117;
  border: 1px solid #30363d;
  color: #c9d1d9;
  border-radius: 4px;
  font-size: 12px;
  font-family: inherit;
  transition: border-color 0.1s;
}
input[type="number"]:focus,
input[type="text"]:focus {
  outline: none;
  border-color: #388bfd;
}

select {
  width: 100%;
  padding: 5px 8px;
  background: #0d1117;
  border: 1px solid #30363d;
  color: #c9d1d9;
  border-radius: 4px;
  font-size: 12px;
  font-family: inherit;
  transition: border-color 0.1s;
  cursor: pointer;
}
select:focus {
  outline: none;
  border-color: #388bfd;
}

.prop-name {
  font-size: 12px;
  font-weight: 600;
  color: #e6edf3;
  margin-bottom: 10px;
}

.divider { height: 1px; background: #21262d; margin: 8px 0; }

.hint {
  font-size: 11px;
  color: #6e7681;
  line-height: 1.9;
}

.multi-hint {
  margin-bottom: 10px;
  padding: 6px 8px;
  border: 1px solid rgba(56, 139, 253, 0.35);
  border-radius: 4px;
  background: rgba(56, 139, 253, 0.12);
  color: #93c5fd;
  font-size: 11px;
  line-height: 1.5;
}

.palette-tabs { display: flex; gap: 6px; }
.palette-tabs .btn { justify-content: center; margin-bottom: 0; }
.sprite-palette p { margin: 6px 0; }
.palette-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  max-height: 260px;
  overflow-y: auto;
}
.palette-sprite {
  padding: 6px;
  border: 1px solid #30363d;
  border-radius: 5px;
  background: #0d1117;
  cursor: grab;
  text-align: center;
  font-size: 11px;
  color: #c9d1d9;
}
.palette-sprite:hover { border-color: #388bfd; background: #1c2d5a; }
.palette-sprite:active { cursor: grabbing; }
.palette-sprite img { display: block; width: 100%; height: 68px; object-fit: contain; margin-bottom: 4px; }
.palette-sprite span { display: block; overflow-wrap: anywhere; }
#canvas-wrapper.sprite-drop-active { outline: 2px solid #388bfd; outline-offset: -2px; }

/* The picture of the variant picked above. */
.variant-preview {
  display: block;
  width: 100%;
  max-height: 150px;
  object-fit: contain;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 4px;
  margin-top: 6px;
}

#canvas-wrapper {
  flex: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;
  background: #0d1117;
}

#canvas {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
}

.hud {
  position: absolute;
  bottom: 10px;
  padding: 3px 9px;
  background: rgba(22, 27, 34, 0.9);
  border: 1px solid #30363d;
  border-radius: 4px;
  font-size: 11px;
  color: #8b949e;
  pointer-events: none;
  line-height: 1.5;
}

#hud-status { left: 10px; max-width: 500px; }
#hud-coords { right: 10px; font-variant-numeric: tabular-nums; }

.export-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(13, 17, 23, 0.55);
  cursor: progress;
}

.export-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 18px 26px;
  background: var(--he-panel-bg);
  border: 1px solid #30363d;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

.export-spinner {
  width: 22px;
  height: 22px;
  border: 2px solid #30363d;
  border-top-color: #388bfd;
  border-radius: 50%;
  animation: export-spin 0.8s linear infinite;
}

@keyframes export-spin {
  to { transform: rotate(360deg); }
}

.export-hint { font-size: 11px; color: #8b949e; }
</style>
